import React, { useState, useEffect } from "react";
import { Box, Stack, Typography, Chip, CircularProgress } from '@mui/material';
import { FileDownload, Check, Close } from "@mui/icons-material";
import { MuiButton, MuiRadio } from '@ace/ace-ui-web';
import { DataGrid, GridColDef, GridRenderCellParams, GridPagination, GridFooterContainer, getGridStringOperators, useGridApiRef, gridFilteredSortedRowIdsSelector } from '@mui/x-data-grid';
import moment from 'moment';
import { toast } from 'sonner';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchTableReport, TableReportDetail } from '../../redux/TableReportReducer/tableReportSlice';
import { fetchGigRequests } from '../../redux/GigRequestsReducer/gigRequestsSlice';
import { GigRequestDetail } from '../../types/gigApi';
import { getDefaultReportStartDate, getDefaultReportEndDate } from '../../utils/reportDateDefaults';
import ReportDateRangeFilter from '../../components/common/ReportDateRangeFilter';
import ResponsiveCardList from '../../components/common/ResponsiveCardList';
import FiltersDrawer from '../../components/common/FiltersDrawer';
import useIsMobile from '../../hooks/useIsMobile';
import { reportDataGridSx } from '../../styles/reportDataGridSx';
import useSkillLabels from '../../hooks/useSkillLabels';

// ========================================
// TYPES
// ========================================
type ShiftStatusFilter = 'ALL' | 'PST' | 'PND' | 'APP' | 'DEN' | 'CAN';

interface TableReportRow {
    id: string;
    requestType: string;
    directReportOnly: boolean | string;
    requestStatus: string;
    postedByUserNm: string;
    claimedByUserNm: string;
    createdDatetime: string;
    requestDt: string;
    weekDay: string;
    startTime: string;
    endTime: string;
    skill: string;
    team: string;
    teamCodes: string[];
    grabbedDatetime: string;
    notes: string;
    approveDatetime: string;
    rejectDatetime: string;
    rejectionNotes: string;
}

// ========================================
// HELPER FUNCTIONS
// ========================================
const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
        'APP': '#4caf50',
        'PST': '#ffc107',
        'PND': '#ff9800',
        'DEN': '#f44336',
        'CAN': '#9e9e9e',
        'Approved': '#4caf50',
        'Posted': '#ffc107',
        'Pending Approval': '#ff9800',
        'Rejected': '#f44336',
        'Cancelled': '#9e9e9e',
    };
    return colors[status] || '#9e9e9e';
};

const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
        'APP': 'Approved',
        'PST': 'Posted',
        'PND': 'Pending Approval',
        'DEN': 'Rejected',
        'CAN': 'Cancelled',
    };
    return labels[status] || status;
};

const getCancellationReason = (claimedByUserNm: string, requestType?: string): string =>
    (claimedByUserNm && claimedByUserNm !== '—') || (requestType && requestType == 'Gig - Request')
        ? 'Expired - Not Approved'
        : 'Expired - Not Claimed';

const buildNotes = (requestStatus: string, claimedByUserNm: string, requestNotes?: string | null, requestType?: string): string => {
    const baseNotes = (requestNotes?.trim()) || '';
    if (requestStatus !== 'CAN') return baseNotes || '—';
    const cancellationReason = getCancellationReason(claimedByUserNm, requestType);
    return baseNotes ? `${baseNotes} | ${cancellationReason}` : cancellationReason;
};

const statusLegend: Array<{ status: string; label: string }> = [
    { status: 'APP', label: 'Approved' },
    { status: 'PST', label: 'Posted' },
    { status: 'PND', label: 'Pending Approval' },
    { status: 'DEN', label: 'Rejected' },
    { status: 'CAN', label: 'Cancelled' },
];

const statusOptions: Array<{ value: ShiftStatusFilter; label: string }> = [
    { value: 'ALL', label: 'All' },
    { value: 'PST', label: 'Posted' },
    { value: 'PND', label: 'Pending Approval' },
    { value: 'APP', label: 'Approved' },
    { value: 'DEN', label: 'Rejected' },
    { value: 'CAN', label: 'Cancelled' },
];

// ========================================
// ROW MAPPER
// ========================================
const mapToRows = (data: TableReportDetail[]): TableReportRow[] =>
    data.map((item, index) => ({
        id: `${item.requestId}-${item.requestSeqId}-${index}`,
        requestType: item.requestType || '—',
        directReportOnly: Boolean(item.directReportOnly),
        requestStatus: item.requestStatus || '',
        requestStatusLabel: getStatusLabel(item.requestStatus || ''),
        postedByUserNm: item.postedByUserNm || '—',
        claimedByUserNm: item.claimedByUserNm || '—',
        createdDatetime: item.createdDatetime || '',
        requestDt: item.requestDt || '',
        weekDay: item.requestDt ? moment(item.requestDt).format('dddd') : '—',
        startTime: item.startTime || '—',
        endTime: item.endTime || '—',
        skill: item.requiredSkill || '—',
        team: item.requiredTeam || '—',
        teamCodes: item.RequiredTeamCodes || [],
        grabbedDatetime: item.grabbedDatetime || '',
        notes: buildNotes(item.requestStatus || '', item.claimedByUserNm || '', item.requestNotes),
        approveDatetime: item.approvedDatetime || '',
        rejectDatetime: item.rejectedDatetime || '',
        rejectionNotes: item.rejectionNotes || '—',
    }));

// GIG requests are always direct-report-only submissions and have no fixed
// start/end time (they're day + hours based), so those columns are marked
// as not applicable ('—'). Each GigRequestDetail row already represents a
// single day of a gig request, so one row here = one table row.
const mapGigToRows = (data: GigRequestDetail[], formatSkills: (raw?: string | null) => string): TableReportRow[] =>
    data.map((item, index) => ({
        id: `gig-${item.requestId}-${item.requestSeqId}-${index}`,
        requestType: 'Gig - Request',
        directReportOnly: '—',
        requestStatus: item.requestStatus || '',
        requestStatusLabel: getStatusLabel(item.requestStatus || ''),
        postedByUserNm: item.requestedByUserNm || '—',
        claimedByUserNm: item.requestedByUserNm || '—',
        createdDatetime: '',
        requestDt: item.requestDt || '',
        weekDay: item.requestDt ? moment(item.requestDt).format('dddd') : '—',
        startTime: item.startTime || '—',
        endTime: item.endTime || '—',
        skill: formatSkills(item.requestedSkillCd) || item.requestedSkillDesc || '—',
        team: item.requestedShiftCd || '—',
        teamCodes: item.requestedShiftCd ? [item.requestedShiftCd] : [],
        grabbedDatetime: '',
        notes: buildNotes(item.requestStatus || '', '', item.requestDetailNotes ?? item.requestNotes, 'Gig - Request'),
        approveDatetime: item.approvedDatetime || '',
        rejectDatetime: item.deniedDatetime || '',
        rejectionNotes: item.rejectionNotes || '—',
    }));

// ========================================
// GRID COLUMNS
// ========================================
const filterOperators = getGridStringOperators().filter(
    (operator) => operator.value !== 'isAnyOf'
);

const columns: GridColDef[] = [
    { field: 'requestDt', headerName: 'Date', flex: 1, filterOperators, valueFormatter: (value: string) => value ? moment(value).format('MM/DD/YYYY') : '—' },
    { field: 'requestType', headerName: 'Type', flex: 1.4, minWidth: 130, filterOperators },
    { field: 'postedByUserNm', headerName: 'Posted By', flex: 1, filterOperators },
    {
        field: 'directReportOnly',
        headerName: 'Direct Reports Only',
        flex: 0.7,
        minWidth: 90,
        headerAlign: 'left',
        align: 'left',
        renderCell: (params: GridRenderCellParams) => (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                {typeof params.value === 'string'
                    ? <Typography variant="body2">{params.value}</Typography>
                    : params.value
                        ? <Check fontSize="small" sx={{ color: '#4caf50' }} />
                        : <Close fontSize="small" sx={{ color: '#f44336' }} />}
            </Box>
        ),
    },
    {
        field: 'requestStatusLabel',
        headerName: 'Status',
        flex: 1,
        minWidth: 120,
        filterOperators,
        renderCell: (params: GridRenderCellParams) => (
            <Chip
                label={params.value}
                size="small"
                sx={{
                    backgroundColor: getStatusColor(getStatusLabel(params.value)),
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                }}
            />
        ),
    },
    { field: 'claimedByUserNm', headerName: 'Worker', flex: 1, filterOperators },
    { field: 'skill', headerName: 'Skill', flex: 1.3, minWidth: 130, filterOperators },
    { field: 'team', headerName: 'Team', flex: 1, filterOperators },
    { field: 'startTime', headerName: 'Start Time', flex: 1, filterOperators },
    { field: 'endTime', headerName: 'End Time', flex: 1, filterOperators },
    { field: 'weekDay', headerName: 'Weekday', flex: 1, filterOperators },
    { field: 'notes', headerName: 'Notes', flex: 2, minWidth: 160, filterOperators },
    { field: 'createdDatetime', headerName: 'Creation Date', flex: 1, filterOperators, valueFormatter: (value: string) => value ? moment(value).format('MM/DD/YYYY') : '—' },
    { field: 'grabbedDatetime', headerName: 'Grabbed', flex: 1, minWidth: 165, filterOperators, valueFormatter: (value: string) => value ? moment(value).format('MM/DD/YYYY h:mm A') : '—' },
    { field: 'approveDatetime', headerName: 'Approved', flex: 1, minWidth: 165, filterOperators, valueFormatter: (value: string) => value ? moment(value).format('MM/DD/YYYY h:mm A') : '—' },
    { field: 'rejectDatetime', headerName: 'Rejected', flex: 1, minWidth: 165, filterOperators, valueFormatter: (value: string) => value ? moment(value).format('MM/DD/YYYY h:mm A') : '—' },
    { field: 'rejectionNotes', headerName: 'Rejection Notes', flex: 2, minWidth: 160, filterOperators }
];

// ========================================
// EXPORT HELPER
// ========================================
const exportToCsv = (rows: TableReportRow[]): void => {
    const headers = [
        'Date', 'Type', 'Posted By', 'Direct Reports Only', 'Status', 'Worker',
        'Skill', 'Team', 'Start Time', 'End Time', 'Weekday', 'Notes',
        'Creation Date', 'Grabbed', 'Approved', 'Rejected', 'Rejection Notes',
    ];

    const csvRows = rows.map((row) => [
        row.requestDt ? moment(row.requestDt).format('MM/DD/YYYY') : '—',
        row.requestType,
        row.postedByUserNm,
        typeof row.directReportOnly === 'string' ? row.directReportOnly : (row.directReportOnly ? 'Yes' : 'No'),
        getStatusLabel(row.requestStatus),
        row.claimedByUserNm,
        row.skill,
        row.team,
        row.startTime,
        row.endTime,
        row.weekDay,
        row.notes,
        row.createdDatetime ? moment(row.createdDatetime).format('MM/DD/YYYY') : '—',
        row.grabbedDatetime ? moment(row.grabbedDatetime).format('MM/DD/YYYY h:mm A') : '—',
        row.approveDatetime ? moment(row.approveDatetime).format('MM/DD/YYYY h:mm A') : '—',
        row.rejectDatetime ? moment(row.rejectDatetime).format('MM/DD/YYYY h:mm A') : '—',
        row.rejectionNotes,
    ]);

    const csvContent = [headers, ...csvRows]
        .map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(','))
        .join('\n');

    // Prepend UTF-8 BOM so Excel renders characters correctly
    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `shift-report-${moment().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

// ========================================
// CUSTOM GRID FOOTER
// ========================================
const CustomFooter = (props: any) => (
    <GridFooterContainer>
        <Box sx={{ flex: 1 }} />
        <MuiButton
            onClick={props.onExport}
            colors="secondary"
            variant="outlined"
            disabled={props.disabled}
            size="small"
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <FileDownload sx={{ fontSize: '1.2rem' }} />
                Export to Excel
            </Box>
        </MuiButton>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <GridPagination />
        </Box>
    </GridFooterContainer>
);

// ========================================
// COMPONENT
// ========================================
const TableReportView: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user: currentUser } = useAppSelector((state) => state.currentUser);
    const { tableData, loading, error } = useAppSelector((state) => state.tableReport);
    const { details: gigDetails, loading: gigLoading, error: gigError } = useAppSelector((state) => state.gigRequests);
    const { formatSkills } = useSkillLabels(currentUser?.siteCd);

    const [statusFilter, setStatusFilter] = useState<ShiftStatusFilter>('ALL');
    const [fromDate, setFromDate] = useState<string>(getDefaultReportStartDate());
    const [toDate, setToDate] = useState<string>(getDefaultReportEndDate());

    useEffect(() => {
        if (currentUser?.siteCd) {
            dispatch(fetchTableReport({
                siteCd: currentUser.siteCd,
                startDate: fromDate || undefined,
                endDate: toDate || undefined,
                statusFilter,
            }));
            // Gig requests don't support 'CAN' on their status filter param, so
            // always pull the full set and apply the status filter client-side
            // alongside the regular-shift rows below.
            dispatch(fetchGigRequests({
                siteCd: currentUser.siteCd,
                statusFilter: 'ALL',
                startDate: fromDate || undefined,
                endDate: toDate || undefined,
            }));
        }
    }, [dispatch, currentUser, fromDate, toDate, statusFilter]);

    useEffect(() => {
        if (error) {
            toast.error(`Failed to load table report: ${error}`);
        }
    }, [error]);

    useEffect(() => {
        if (gigError) {
            toast.error(`Failed to load gig report data: ${gigError}`);
        }
    }, [gigError]);

    const apiRef = useGridApiRef();
    const filteredGigDetails = statusFilter === 'ALL'
        ? gigDetails
        : gigDetails.filter((d) => (d.requestStatus || '').toUpperCase() === statusFilter);
    const rows = [...mapToRows(tableData), ...mapGigToRows(filteredGigDetails, formatSkills)].sort((a, b) => {
        const aTime = a.requestDt ? moment(a.requestDt).valueOf() : 0;
        const bTime = b.requestDt ? moment(b.requestDt).valueOf() : 0;
        return bTime - aTime;
    });
    const isMobile = useIsMobile('md');
    const isLoading = loading || gigLoading;

    const handleExportToExcel = () => {
        const filteredIds = gridFilteredSortedRowIdsSelector(apiRef);
        const filteredRows = rows.filter((row) => filteredIds.includes(row.id));
        exportToCsv(filteredRows);
    };

    const statusRadios = (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ maxWidth: '100%', overflowX: { xs: 'visible', sm: 'auto' } }}
        >
            {statusOptions.map((option) => (
                <MuiRadio
                    key={option.value}
                    name="statusFilter"
                    label={option.label}
                    value={option.value}
                    isChecked={statusFilter === option.value}
                    onChange={() => setStatusFilter(option.value)}
                    color="acePrimary"
                />
            ))}
        </Stack>
    );

    let dataView: React.ReactNode;

    if (isLoading) {
        dataView = (
            <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: '200px' }}>
                <CircularProgress />
                <Typography variant="body1">Loading report data...</Typography>
            </Stack>
        );
    } else if (isMobile) {
        dataView = (
            <Stack spacing={1.5}>
                <ResponsiveCardList
                    rows={rows}
                    getRowId={(row) => row.id}
                    primary={(row) => (
                        row.requestDt ? moment(row.requestDt).format('ddd, MMM D, YYYY') : '—'
                    )}
                    secondary={(row) => (
                        <Chip
                            label={getStatusLabel(row.requestStatus)}
                            size="small"
                            sx={{
                                backgroundColor: getStatusColor(row.requestStatus),
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                            }}
                        />
                    )}
                    fields={[
                        { label: 'Type', field: 'requestType' },
                        { label: 'Time', render: (row) => `${row.startTime} – ${row.endTime}` },
                        { label: 'Worker', field: 'claimedByUserNm' },
                        { label: 'Skill', field: 'skill' },
                        { label: 'Team', field: 'team' },
                        { label: 'Posted By', field: 'postedByUserNm' },
                        { label: 'Notes', field: 'notes' },
                    ]}
                    expandableFields={[
                        { label: 'Weekday', field: 'weekDay' },
                        {
                            label: 'Direct Reports Only',
                            render: (row) => (
                                typeof row.directReportOnly === 'string'
                                    ? row.directReportOnly
                                    : row.directReportOnly
                                        ? <Check fontSize="small" sx={{ color: '#4caf50' }} />
                                        : <Close fontSize="small" sx={{ color: '#f44336' }} />
                            ),
                        },
                        {
                            label: 'Creation Date',
                            render: (row) => row.createdDatetime
                                ? moment(row.createdDatetime).format('MM/DD/YYYY')
                                : '—',
                        },
                        {
                            label: 'Grabbed',
                            render: (row) => row.grabbedDatetime
                                ? moment(row.grabbedDatetime).format('MM/DD/YYYY h:mm A')
                                : '—',
                        },
                        {
                            label: 'Approved',
                            render: (row) => row.approveDatetime
                                ? moment(row.approveDatetime).format('MM/DD/YYYY h:mm A')
                                : '—',
                        },
                        {
                            label: 'Rejected',
                            render: (row) => row.rejectDatetime
                                ? moment(row.rejectDatetime).format('MM/DD/YYYY h:mm A')
                                : '—',
                        },
                        { label: 'Rejection Notes', field: 'rejectionNotes' },
                    ]}
                />
                <MuiButton
                    onClick={() => exportToCsv(rows)}
                    colors="secondary"
                    variant="outlined"
                    disabled={rows.length === 0}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <FileDownload sx={{ fontSize: '1.2rem' }} />
                        Export to Excel
                    </Box>
                </MuiButton>
            </Stack>
        );
    } else {
        dataView = (
            <DataGrid
                apiRef={apiRef}
                rows={rows}
                columns={columns}
                autosizeOnMount={true}
                initialState={{
                    pagination: {
                        paginationModel: { pageSize: 10 },
                    },
                }}
                pageSizeOptions={[5, 10, 25, 50]}
                disableRowSelectionOnClick
                slots={{ footer: CustomFooter }}
                slotProps={{
                    footer: {
                        onExport: handleExportToExcel,
                        disabled: isLoading || rows.length === 0,
                    } as any,
                }}
                sx={{
                    ...reportDataGridSx,
                    height: 'calc(100vh - 320px)',
                    minHeight: 300,
                }}
            />
        );
    }

    return (
        <Box sx={{ width: '100%', p: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Filter Bar */}
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
            >
                <ReportDateRangeFilter
                    fromDate={fromDate}
                    toDate={toDate}
                    onFromDateChange={setFromDate}
                    onToDateChange={setToDate}
                />

                {isMobile ? (
                    <FiltersDrawer
                        title="Status Filter"
                        triggerLabel="Status"
                        activeCount={statusFilter !== 'ALL' ? 1 : 0}
                    >
                        {statusRadios}
                    </FiltersDrawer>
                ) : (
                    statusRadios
                )}
            </Stack>

            {/* Data view */}
            <Box sx={{ width: '100%', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {dataView}
            </Box>

            {/* Status Legend */}
            <Box sx={{
                mt: 1.5,
                py: 1,
                px: 1.5,
                backgroundColor: '#f9f9f9',
                borderRadius: '6px',
                border: '1px solid #e0e0e0',
                flexShrink: 0,
            }}>
                <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', fontSize: '0.8125rem', mr: 1 }}>
                        Status:
                    </Typography>
                    {statusLegend.map((item) => (
                        <Stack key={item.status} direction="row" alignItems="center" spacing={0.75}>
                            <Box
                                sx={{
                                    width: '14px',
                                    height: '14px',
                                    backgroundColor: getStatusColor(item.status),
                                    borderRadius: '3px',
                                    flexShrink: 0,
                                }}
                            />
                            <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: '#666' }}>
                                {item.label}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Box>
        </Box>
    );
};

export default TableReportView;